import cloneDeep from 'lodash-es/cloneDeep';

import { AppState, initialState, STATE_LOCAL_STORAGE_KEY } from '~/app-store';
import { LoginConstants } from '~/login/login-constants';
import { OKTA_TOKEN_STORAGE } from '~/login/okta-login';
import { Logger } from '~/utils/logger';

/**
 * Gets an item from the `localStorage`
 * @param key {string}	`localStorage` key
 */
export const getItem = (key: string): any => {
	if (localStorage) {
		return localStorage.getItem(key);
	} else {
		Logger.warn('Unable to retrieve localStorage');
	}
};

/**
 * Sets an item to the `localStorage`
 * @param key {string}	`localStorage` key
 * @param item {any}	Item to be stored
 */
export const setItem = (key: string, item: any) => {
	if (localStorage) {
		localStorage.setItem(key, item);
	} else {
		Logger.warn('Unable to retrieve localStorage');
	}
};

/**
 * Removes an item from the `localStorage`
 * @param key {string}	`localStorage` key
 */
export const removeItem = (key: string) => {
	if (localStorage) {
		localStorage.removeItem(key);
	} else {
		Logger.warn('Unable to retrieve localStorage');
	}
};

/**
 * Clears all items from the `localStorage`
 */
export const clearAllLocalData = () => {
	if (localStorage) {
		localStorage.clear();
	} else {
		Logger.warn('Unable to retrieve localStorage');
	}
};

/**
 * Clears Login related stuff from the `localStorage`
 */
export const clearLocalLoginData = () => {
	removeItem(LoginConstants.ACCESS_TOKEN);
	removeItem(LoginConstants.REFRESH_TOKEN);
	removeItem(LoginConstants.EXPIRE_TIME);
	removeItem(LoginConstants.REFRESHING_TOKEN);
	removeItem(OKTA_TOKEN_STORAGE);
	resetLocalState();
};

/**
 * Resets the local state from the `localStorage`
 */
export const resetLocalState = () => {
	const freshState = cloneDeep(initialState);

	// But let's keep the bookmarks and history
	const oldState = JSON.parse(getItem(STATE_LOCAL_STORAGE_KEY)) as AppState;
	if (!oldState) {
		setItem(
			STATE_LOCAL_STORAGE_KEY,
			JSON.stringify(cloneDeep(initialState)),
		);
		return;
	}

	freshState.navigation.bookmarks = cloneDeep(oldState.navigation.bookmarks);
	freshState.navigation.history = cloneDeep(oldState.navigation.history);
	freshState.navigation.searchHistory = cloneDeep(
		oldState.navigation.searchHistory,
	);

	setItem(STATE_LOCAL_STORAGE_KEY, JSON.stringify(freshState));
};

/**
 * Clears the local state from the `localStorage`
 */
export const clearLocalState = () => {
	removeItem(STATE_LOCAL_STORAGE_KEY);
};
